import { createI18n } from 'vue-i18n'
import { ref, computed } from 'vue'
// vant语言包
import vantPtBR from 'vant/es/locale/lang/pt-BR'
import vantEsES from 'vant/es/locale/lang/es-ES'
import { Locale } from 'vant'
import type { PickerColumn } from 'vant'
import { STORAGE_LANG_KEY } from '@/stores/mutation-type'

type AppLocale = 'es-MX' | 'pt-BR'

const FALLBACK_LOCALE = 'pt-BR'

const vantLocales = {
  'pt-BR': vantPtBR,
  'es-MX': vantEsES,
}

export const languageColumns: PickerColumn = [
  { text: 'Brazil', value: 'pt-BR' },
  { text: 'Español', value: 'es-MX' },
]
// 语言枚举
export const langEnums = {
  PT_BR: 'pt-BR',
  ES_MX: 'es-MX',
}

// 请求头/服务端 国际化
export const SERVICE_PT = 'pt-pt'
export const SERVICE_MX = langEnums.ES_MX.toLocaleLowerCase()
export const serviceLocales = {
  [langEnums.PT_BR]: SERVICE_PT,
  [langEnums.ES_MX]: SERVICE_MX,
}

// 获取初始语言
const initialLocale = getI18nLocale() || FALLBACK_LOCALE

// 根据文件路径生成命名空间
function generateNamespace(filePath: string, locale: string): string | null {
  const relativePath = filePath.replace(`../locales/${locale}/`, '').replace(/\.json$/, '')
  const pathParts = relativePath.split('/')

  if (pathParts.length === 1 && pathParts[0] === 'index') {
    return null
  }

  const fileName = pathParts.pop()
  const dirPath = pathParts.join('-')

  if (dirPath) {
    return `${dirPath}-${fileName}`
  } else {
    return fileName
  }
}

export const i18nLoading = ref(false)
export const i18nLoaded = ref(false)

// 同步加载所有支持的语言包
function loadAllLocalesSync() {
  const supportedLocales = ['pt-BR', 'es-MX'] as const
  const allMessages: Record<string, any> = {}

  // 使用静态的 glob 模式匹配所有语言文件
  const allModules = import.meta.glob('../locales/**/*.json', {
    eager: true,
  })

  console.log('All modules loaded:', Object.keys(allModules).length)

  // 初始化每个语言的消息对象
  supportedLocales.forEach((locale) => {
    allMessages[locale] = {}
  })

  // 遍历所有模块，按语言分类
  for (const modulePath in allModules) {
    const module = allModules[modulePath] as { default: any }

    // 从路径中提取语言代码
    for (const locale of supportedLocales) {
      if (modulePath.includes(`/${locale}/`)) {
        const namespace = generateNamespace(modulePath, locale)

        if (namespace === null) {
          Object.assign(allMessages[locale], module.default)
        } else {
          allMessages[locale][namespace] = module.default
        }
        break // 找到对应语言后跳出循环
      }
    }
  }

  // 检查每个语言是否加载了消息
  for (const locale of supportedLocales) {
    if (Object.keys(allMessages[locale]).length === 0) {
      console.warn(`No messages found for locale: ${locale}`)
    } else {
      console.log(`Locale ${locale} loaded with ${Object.keys(allMessages[locale]).length} namespaces`)
    }
  }

  return allMessages
}

// 同步加载所有语言包
let allMessages = {}
try {
  allMessages = loadAllLocalesSync()
  i18nLoaded.value = true
} catch (error) {
  console.error('Failed to load all locales:', error)
  allMessages = {
    [initialLocale]: {},
    [FALLBACK_LOCALE]: {},
  }
}

// 创建i18n实例，包含所有支持的语言
export const i18n = createI18n({
  locale: initialLocale,
  fallbackLocale: FALLBACK_LOCALE,
  legacy: false,
  messages: allMessages,
})

console.log('i18n created with messages for:', Object.keys(allMessages))

// 设置vant语言包
Locale.use(initialLocale, vantLocales[initialLocale as AppLocale])

export const locale = computed({
  get() {
    return i18n.global.locale.value
  },
  set(language: AppLocale) {
    setLang(language)
    i18n.global.locale.value = language
  },
})

export const isBrazil = computed(() => {
  return locale.value === langEnums.PT_BR
})

export const isMX = computed(() => locale.value === langEnums.ES_MX)

export async function setLang(lang: AppLocale) {
  i18nLoading.value = true
  try {
    // 所有语言包都已加载，直接切换
    document.querySelector('html')?.setAttribute('lang', lang)
    //
    console.log('lang:', lang)

    localStorage.setItem(STORAGE_LANG_KEY, lang)
    Locale.use(lang, vantLocales[lang])
  } finally {
    i18nLoading.value = false
  }
}

// 获取当前语言对应的语言包名称
function getI18nLocale() {
  const storedLocale = localStorage.getItem(STORAGE_LANG_KEY) || navigator.language
  const langs = languageColumns.map((v) => v.value as string)
  const foundLocale = langs.find((v) => v === storedLocale || v.indexOf(storedLocale) === 0)
  const locale = foundLocale || FALLBACK_LOCALE

  return locale
}
